import React from 'react';

import { noop } from '@/utils';
import KeyPressPromptButton from '@/components/primitive/keypress-prompt-button';

import SearchSuggestion from '../search-suggestion';
import CrossCircleIconButton from '../cross-circle-icon-button';
import { SearchableType } from '../types';
import { inputStore } from '../stores/input';
import { queryStore } from '../stores/query';

import * as styles from './styles';

function isValueAutoCompletable(
  scopes: Set<SearchableType>,
  value: string
): value is SearchableType {
  return (
    // NOTE: that matcher after scope, "=" i this case, much match whatever is shown in <Scope /> component
    scopes.has(value as SearchableType) || [...scopes].some(scope => `${scope}:` === `${value}`)
  );
}

type SearchInputHandle = {
  focus: typeof noop;
  blur: typeof noop;
};

type SearchInputProps = {
  scopes: Set<SearchableType>;
  forwardedRef: React.RefObject<SearchInputHandle>;
  isInputFocused: boolean;
  setIsInputFocused: React.Dispatch<React.SetStateAction<boolean>>;
};

const SearchInput: React.ForwardRefRenderFunction<SearchInputHandle, SearchInputProps> = (
  { scopes, forwardedRef, isInputFocused, setIsInputFocused = noop },
  _ref: React.Ref<HTMLInputElement>
) => {
  const inputStoreContext = React.useContext(inputStore);
  const queryStoreContext = React.useContext(queryStore);

  const { inputValue, isAutoCompleting } = inputStoreContext.state;
  const { setInputValue, clearInputValue, setAutoCompleting } = inputStoreContext.actions;
  const { addParam } = queryStoreContext.actions;

  function onChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;

    setInputValue(value);

    if (!isValueAutoCompletable(scopes, value)) {
      if (isAutoCompleting) {
        setAutoCompleting(false);
      }
      return;
    }

    setAutoCompleting(true);
  }

  const onSubmitHandler = (data: any) => {
    const { nextTerms, scope } = data;
    if (!nextTerms) return;

    addParam({ terms: nextTerms, scope });
    clearInputValue();
  };

  const inputRef = React.useRef<HTMLInputElement>(document.createElement('input'));

  React.useImperativeHandle(forwardedRef, () => ({
    focus() {
      inputRef.current.focus();
    },
    blur() {
      inputRef.current.blur();
    },
  }));

  return (
    <div css={styles.root}>
      {!isInputFocused && inputValue.length === 0 && (
        <KeyPressPromptButton onClick={() => setIsInputFocused(true)}>/</KeyPressPromptButton>
      )}
      <input
        ref={inputRef}
        type="search"
        id="searchInput"
        size={inputValue.length || 80}
        value={inputValue}
        onChange={event => onChangeHandler(event)}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
        placeholder="Add a scope to your search, e.g. team: / product: / capability: "
      />

      {inputValue.length > 0 && isAutoCompleting && (
        <>
          <SearchSuggestion
            scope={inputValue as SearchableType}
            onSubmitHandler={onSubmitHandler}
          />
          <CrossCircleIconButton clearInputValue={clearInputValue} />
        </>
      )}
    </div>
  );
};

export default React.forwardRef(SearchInput);
